import type {
  IManagementReportMonth,
  RootfiReportSection,
} from '@commenda/api';

import type {
  BalanceSheetSection,
  ILedgerLine,
  IncomeStatementSection,
  LedgerSection,
  LedgerStatement,
} from '../types/buckets';

export interface ILedgerLineValue {
  line: ILedgerLine;
  value: number;
}

const INCOME_STATEMENT_SECTIONS: IncomeStatementSection[] = [
  'revenue',
  'cost_of_goods_sold',
  'operating_expenses',
  'non_operating_revenue',
  'non_operating_expenses',
];

const BALANCE_SHEET_SECTIONS: BalanceSheetSection[] = [
  'assets',
  'liabilities',
  'equity',
];

/** Flattens nested RootFi statement sections into leaf ledger lines. */
export class LedgerHelper {
  /**
   * Stable id for a leaf line: the section plus the full name path, so the same
   * account keys identically across months even when the ERP supplies no account ids.
   */
  static lineId(
    section: LedgerSection,
    path: string[],
    leafName: string
  ): string {
    return `path:${section}/${[...path, leafName].join('/')}`;
  }

  /** Flatten a month's P&L and balance sheet into leaf lines with values. */
  static flattenMonth(month: IManagementReportMonth): ILedgerLineValue[] {
    return [
      ...INCOME_STATEMENT_SECTIONS.flatMap((section) =>
        LedgerHelper.walk(month.incomeStatement[section], 'PNL', section, [])
      ),
      ...BALANCE_SHEET_SECTIONS.flatMap((section) =>
        LedgerHelper.walk(month.balanceSheet[section], 'BS', section, [])
      ),
    ];
  }

  /** Union of all ledger lines seen across months; a line may only appear in some months. */
  static collectLines(months: IManagementReportMonth[]): ILedgerLine[] {
    const seen = new Map<string, ILedgerLine>();
    months
      .flatMap((month) => LedgerHelper.flattenMonth(month))
      .forEach(({ line }) => {
        if (!seen.has(line.id)) seen.set(line.id, line);
      });
    return [...seen.values()];
  }

  /** Sum of top-level section values, i.e. the section total the exported statement shows. */
  static sectionTotal(
    sections: RootfiReportSection[] | null | undefined
  ): number {
    return (sections ?? []).reduce((sum, s) => sum + (s.value ?? 0), 0);
  }

  private static walk(
    sections: RootfiReportSection[] | null | undefined,
    statement: LedgerStatement,
    section: LedgerSection,
    path: string[]
  ): ILedgerLineValue[] {
    return (sections ?? []).flatMap((node) => {
      const children = node.line_items ?? [];
      if (children.length > 0) {
        return LedgerHelper.walk(children, statement, section, [
          ...path,
          node.name,
        ]);
      }
      return [
        {
          line: {
            id: LedgerHelper.lineId(section, path, node.name),
            name: node.name,
            statement,
            section,
            path,
          },
          value: node.value ?? 0,
        },
      ];
    });
  }
}
